import { Button, DialogActions } from "@mui/material";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

export type CancelOrProceedActionsProps = {
  /** The way out, leaving without doing anything, whatever it is labelled. */
  cancel: {
    /** Defaults to "Cancel". */
    label?: string;
    onClick: () => Promisable<void>;
    disabled?: boolean;
  };
  /** The dialog's one action, whatever it is labelled. */
  proceed: {
    label: string;
    onClick: () => Promisable<void>;
    disabled?: boolean;
    /** `destructive` for an action that removes, ends or undoes something. */
    kind: "primary" | "destructive";
  };
};

/**
 * A dialog's closing choice, back out or go ahead, drawn to the design
 * library so that a dialog does not choose its own buttons. Deliberately
 * these two and no more: a row offering anything else, such as a step back
 * or a third button, composes `DialogActions` itself.
 */
export const CancelOrProceedActions = ({
  cancel,
  proceed,
}: CancelOrProceedActionsProps) => {
  const { t } = useTranslation();

  return (
    <DialogActions>
      <Button
        variant="outlined"
        disabled={cancel.disabled}
        onClick={() => {
          void cancel.onClick();
        }}
      >
        {cancel.label ?? t("common.cancel")}
      </Button>
      <Button
        variant="contained"
        color={match(proceed.kind)
          .with("primary", () => "primary" as const)
          .with("destructive", () => "error" as const)
          .exhaustive()}
        disabled={proceed.disabled}
        onClick={() => {
          void proceed.onClick();
        }}
      >
        {proceed.label}
      </Button>
    </DialogActions>
  );
};
